import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { injectIntl, FormattedMessage } from '../../../../components/Intl/withIntl.js';
import { JSONLD, Generic, GenericCollection } from 'react-structured-data';

import config from '../../../../config.js';

import { getLanguagePath } from '../../../Intl/IntlHelper.js';
import { getConfig } from '../../../Config/ConfigActions.js';
import { getPanelBySlugAndLanguage, getContentBlockItemValue, memoizedGetPanelBySlugAndLanguage } from '../../../Cms/CmsActions.js';

import withRouter from '../../../../components/Router/WithRouter.js';
import PredictiveLink from '../../../Cms/components/view/PredictiveLink.js';

// `hiddenCrumbs` are appended to the BreadcrumbList JSON-LD only, never to the visible trail. Used by
// the product page to close the chain with the product itself (Google expects the current page as the
// last item) without duplicating the title that already appears as the h1 right below.
function Breadcrumbs({ location, intl, crumbs = [], hiddenCrumbs = [], breadcrumbBaseAnchorPanel = null }) {
    const breadcrumbBaseAnchor = breadcrumbBaseAnchorPanel && getContentBlockItemValue(breadcrumbBaseAnchorPanel.blocks, 'text');

    // Callers pass `!breadcrumbs.length && title`, so a falsy placeholder can sit in the array and
    // would otherwise render an empty span plus a trailing " > ". Drop them before rendering.
    const visibleCrumbs = crumbs.filter(Boolean);

    const renderCrumb = (crumb, index) => {
        return (
            <span key={index}>
                {
                    typeof crumb === 'object'
                    ? <a href={crumb.path} className={classnames()}>{crumb.text}</a>
                    : <span className={classnames('text-white')}>{crumb}</span>
                }
                {index + 1 < visibleCrumbs.length && ' > '}
            </span>
        );
    };

    // console.log(location, location.pathname.split('/'));
    // Enabled on all pages except home & cart
    const pathParts = location.pathname.split('/').filter(part => part);
    const isEnabled = pathParts.length > 1 && !location.pathname.includes('/cart');

    const homeText = breadcrumbBaseAnchor || intl.formatMessage({ id: 'appHome', defaultMessage: 'Home' });

    if(isEnabled && visibleCrumbs.length) {
        // Schema itemListElement, prefixed with Home so the BreadcrumbList is complete.
        const schemaCrumbs = [{ text: homeText, path: getLanguagePath('/', intl.locale) }]
            .concat(crumbs.map(crumb => (crumb.path ? crumb : { text: crumb })))
            .concat(hiddenCrumbs.map(crumb => (crumb && crumb.path ? crumb : { text: crumb })))
            .filter(crumb => crumb.text)
            .map(crumb => ({ ...crumb, url: crumb.path ? `${config.url.slice(0, -1)}${crumb.path}` : '' }));

        return (
            <Fragment>
                <JSONLD dangerouslyExposeHtml>
                    <Generic jsonldtype="BreadcrumbList">
                        <GenericCollection type="itemListElement">
                            {schemaCrumbs.map((crumb, index) => (
                                <Generic
                                    key={crumb.url || crumb.text}
                                    jsonldtype="ListItem"
                                    schema={{
                                        position: index + 1,
                                        // Last item (current page) usually has no url: expose the name directly, per schema.org guidance.
                                        ...(crumb.url
                                            ? { item: { '@id': crumb.url, name: crumb.text, url: crumb.url } }
                                            : { name: crumb.text }),
                                    }}
                                />
                            ))}
                        </GenericCollection>
                    </Generic>
                </JSONLD>
                <div className="breadcrumbs p-2 text-primary">
                    <a href={getLanguagePath('/', intl.locale)} className="">{breadcrumbBaseAnchor || <FormattedMessage id="appHome" defaultMessage="Home" />}</a>
                    {' > '}
                    {visibleCrumbs.map(renderCrumb)}
                </div>
            </Fragment>
        );
    }
    return null;
}

function mapStateToProps(store, props) {
    return {
        breadcrumbBaseAnchorPanel: memoizedGetPanelBySlugAndLanguage(store, 'breadcrumb-base-anchor', (store.intl || {}).locale),
    };
}

Breadcrumbs.propTypes = {
    location: PropTypes.object.isRequired,
    intl: PropTypes.object.isRequired,
    crumbs: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
    hiddenCrumbs: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
    breadcrumbBaseAnchorPanel: PropTypes.object,
};

export default connect(mapStateToProps)(withRouter(injectIntl(Breadcrumbs)));
